import { encode, decode } from '@jsquash/jxl';
import { getSDK as getUnlinkSDK } from '../sdk/UnlinkInstance';
import { loadLocalKeys } from '../db/KeyStorageManager';
import dispatchToChat from "../services/message/DispatchToChat";
import { FEES, MESSAGES_TYPES } from '../Config';
import { sendInstructionsWithRetry} from '../utils/SendInstructions';
import { upsertRatchetKeys, getRatchetingKeys } from '../db/RatchetedConversationKeys';
import bs58 from "bs58";

/**
 * Validates that the selected file is an image and below the given size (default 10KB).
 */
function validateImage(file, maxSizeKB = 40) {
  if (!file) return { valid: false, error: "No file selected." };

  const allowedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: "Unsupported file type." };
  }

  if (file.size > maxSizeKB * 1024) {
    return { valid: false, error: `Image must be under ${maxSizeKB}KB.` };
  }

  return { valid: true };
}

/**
 * Converts an image File (e.g. JPEG, PNG) into ImageData.
 */
function fileToImageData(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context is not available"));
        return;
      }

      ctx.drawImage(img, 0, 0);
      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        resolve(imageData);
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Compresses ImageData to JPEG XL and returns Uint8Array.
 */
async function compressImageToJXLUint8(imageData, options = {}) {
  const buffer = await encode(imageData, options);
  return new Uint8Array(buffer);
}

/**
 * Decompresses JPEG XL Uint8Array to ImageData-compatible object.
 */
async function decompressJXLToImageData(jxlBytes) {
  const decoded = await decode(jxlBytes.buffer);
  return decoded;
}

/**
 * Compresses, encrypts, and sends an image using the Unlink protocol.
 * 
 * @param {Object} params
 * @param {File} params.file - The image file selected by the user
 * @param {Uint8Array} params.conversationId
 * @param {Uint8Array} params.contactIdentityViewPublicKey
 * @param {Uint8Array} params.contactNextViewPublicKey
 * @param {Uint8Array} params.kyberSecret
 * @param {string} params.userId
 * @param {string} params.password
 * @param {Uint8Array} params.salt
 */
export async function sendImageMessage({
  file,
  conversationId,
  contactIdentityViewPublicKey,
  contactNextViewPublicKey,
  kyberSecret,
  userId,
  password,
  salt,
}) {
  const { valid, error } = validateImage(file, 40); // e.g. 40KB max
  if (!valid) throw new Error(error);

  const unlinkSdk = getUnlinkSDK();

  const {
    viewPublicKey: localViewPublicKey,
    viewPrivateKey: localViewPrivateKey,
  } = await loadLocalKeys({ userId, password, salt });

  const keypair = await unlinkSdk.generateUnlinkKeypair();
  const localNextViewPublicKey = keypair.viewPublicKey;
  const localNextViewPrivateKey = keypair.viewPrivateKey;

  const filterTag = unlinkSdk.deriveRandomFilterTag({
    viewKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
    bits: 3,
  });

  // Compress image to JXL
  const imageData = await fileToImageData(file);
  const compressed = await compressImageToJXLUint8(imageData, { quality: 85 });

  const payload = serializeMediaPayload({
    version: 1,
    fileName: "cat.jpg",
    mimeType: "image/jpeg",
    bytes: compressed,
  });

  // Generate Solana instructions
  const { instructions, messageId } = await unlinkSdk.createDataSendInstructions({
    receiverViewPublicKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
    senderViewPublicKey: localViewPublicKey,
    senderViewPrivateKey: localViewPrivateKey,
    senderNextViewPublicKey: localNextViewPublicKey,
    data: payload,
    messageType: MESSAGES_TYPES.MESSAGE_TYPE_MEDIA,
    spamMitigationFeeLamports: FEES.SPAM_MITIGATION_LAMPORTS,
    conversationId,
    kyberSecret,
    filterTag,
  });

  // Dispatch local preview of image to chat
  dispatchToChat({
    message: {
      parsed: {
        version: 1,
        bytes: compressed,
        mimeType: file.type,
        fileName: file.name,
      },
      receiverViewPublicKey: contactNextViewPublicKey || contactIdentityViewPublicKey,
      senderViewPublicKey: localViewPublicKey,
      senderNextViewPublicKey: localNextViewPublicKey,
      conversationId,
      messageId,
      messageType: MESSAGES_TYPES.MESSAGE_TYPE_MEDIA,
    },
    receiverIdentityViewPublicKey: contactIdentityViewPublicKey,
    userId,
    password,
    salt,
  });

  await sendInstructionsWithRetry({
    instructions,
    networkFeeLamports: FEES.NETWORK_FEE_LAMPORTS,
    password,
    userId,
    salt,
  });

  await upsertRatchetKeys({
    conversationId,
    contactIdentityViewPublicKey,
    localNextViewPublicKey,
    localNextViewPrivateKey,
    userId,
    password,
  });
}

/**
 * Converts compressed JPEG XL Uint8Array back into a Blob URL for display.
 *
 * @param {Uint8Array} jxlBytes - The compressed JPEG XL data
 * @returns {Promise<string>} Blob URL usable in <img src={...}>
 * @throws {Error} If decompression fails
 */
export async function getImage(jxlBytes) {
  if (!(jxlBytes instanceof Uint8Array)) {
    throw new Error("Input must be a Uint8Array");
  }

  const { width, height, data } = await decode(jxlBytes.buffer);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");

  const imageData = new ImageData(new Uint8ClampedArray(data), width, height);
  ctx.putImageData(imageData, 0, 0);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error("Failed to convert canvas to blob");
      }
      const url = URL.createObjectURL(blob);
      resolve(url);
    }, "image/png"); // or "image/jpeg" if needed
  });
}

/**
 * Serializes media payload into binary format.
 * Format:
 * - 1 byte: version
 * - 2 bytes: filename length
 * - N bytes: UTF-8 filename
 * - 2 bytes: mime type length
 * - N bytes: UTF-8 mime type
 * - 4 bytes: content length
 * - N bytes: raw compressed image data (e.g. JPEG XL)
 */
export function serializeMediaPayload({ version, fileName, mimeType, bytes }) {
  const encoder = new TextEncoder();
  const nameBytes = encoder.encode(fileName || "");
  const mimeBytes = encoder.encode(mimeType || "");

  const totalLength =
    1 + 2 + nameBytes.length + 2 + mimeBytes.length + 4 + bytes.length;

  const buffer = new Uint8Array(totalLength);
  let offset = 0;

  // Version
  buffer[offset++] = version;

  // File name
  buffer[offset++] = (nameBytes.length >> 8) & 0xff;
  buffer[offset++] = nameBytes.length & 0xff;
  buffer.set(nameBytes, offset);
  offset += nameBytes.length;

  // Mime type
  buffer[offset++] = (mimeBytes.length >> 8) & 0xff;
  buffer[offset++] = mimeBytes.length & 0xff;
  buffer.set(mimeBytes, offset);
  offset += mimeBytes.length;

  // Compressed data length (4 bytes, big-endian)
  buffer[offset++] = (bytes.length >> 24) & 0xff;
  buffer[offset++] = (bytes.length >> 16) & 0xff;
  buffer[offset++] = (bytes.length >> 8) & 0xff;
  buffer[offset++] = bytes.length & 0xff;

  // Image data
  buffer.set(bytes, offset);

  return buffer;
}

/**
 * Deserializes binary media payload back into components.
 * Returns: { version, fileName, mimeType, bytes }
 */
export function deserializeMediaPayload(payload) {
  const decoder = new TextDecoder();
  let offset = 0;

  const version = payload[offset++];

  // File name
  const nameLen = (payload[offset++] << 8) | payload[offset++];
  const fileName = decoder.decode(payload.slice(offset, offset + nameLen));
  offset += nameLen;

  // Mime type
  const mimeLen = (payload[offset++] << 8) | payload[offset++];
  const mimeType = decoder.decode(payload.slice(offset, offset + mimeLen));
  offset += mimeLen;

  // Data length
  const dataLen =
    (payload[offset++] << 24) |
    (payload[offset++] << 16) |
    (payload[offset++] << 8) |
    payload[offset++];

  const bytes = payload.slice(offset, offset + dataLen);

  return { version, fileName, mimeType, bytes };
}

